/**
 * Adapter between the admin UI's legacy auth shape and the live backend
 * IAM contract (M02 §4 /v1/auth/*).
 *
 * Why this exists
 * ───────────────
 * The UI was originally wired against a MSW mock that returned
 *   { requiresMfa, mfaChallengeId, token, user }
 * and accepted body { username, password }. The real backend (NestJS,
 * AuthnController @Controller('v1/auth')) returns
 *   { success, mfa_required, mfa_token, mfa_method, expires_in,
 *     data: { accessToken, refreshToken, tokenType, expiresIn,
 *             sessionId, userId } }
 * and expects body { identifier, password, deviceType, ... }.
 *
 * `authApi.login()` / `verifyMfa()` / `refresh()` normalise both shapes
 * down to the legacy internal type so the rest of the UI is untouched.
 * MSW tests continue to register the legacy shape; the live path uses the
 * real shape. Both work.
 *
 * Defect coverage: INT-001, INT-002, INT-018.
 */

import { apiRequest, ApiError } from '../api/client.js';
import type { AuthUser } from './AuthProvider.js';

/* ── Internal (legacy) shape used by AuthProvider/UI ─────────────────── */

export interface NormalizedLogin {
  readonly requiresMfa: boolean;
  readonly mfaChallengeId: string | null;
  readonly mfaMethod: string | null;
  readonly token: string | null;
  readonly refreshToken: string | null;
  readonly user: AuthUser | null;
}

export interface NormalizedVerify {
  readonly token: string;
  readonly refreshToken: string | null;
  /** Null when the wire response carries no identity; callers MUST keep the user they already have rather than invent one. */
  readonly user: AuthUser | null;
}

/* ── Wire shapes ─────────────────────────────────────────────────────── */

interface BackendTokenData {
  readonly accessToken: string;
  readonly refreshToken?: string;
  readonly tokenType?: string;
  readonly expiresIn?: number;
  readonly sessionId?: string;
  readonly userId?: string;
  readonly user?: AuthUser;
}

interface BackendLoginResponse {
  readonly success?: boolean;
  readonly mfa_required?: boolean;
  readonly mfa_token?: string;
  readonly mfa_method?: string;
  readonly expires_in?: number;
  readonly data?: BackendTokenData;
}

interface LegacyLoginResponse {
  readonly requiresMfa?: boolean;
  readonly mfaChallengeId?: string;
  readonly token?: string;
  readonly user?: AuthUser;
}

type LoginWire = BackendLoginResponse & LegacyLoginResponse;

interface BackendVerifyResponse {
  readonly success?: boolean;
  readonly data?: BackendTokenData;
}

interface LegacyVerifyResponse {
  readonly token?: string;
  readonly user?: AuthUser;
}

type VerifyWire = BackendVerifyResponse & LegacyVerifyResponse;

/* ── Helpers ─────────────────────────────────────────────────────────── */

function userFromTokenData(data: BackendTokenData, fallbackUsername: string): AuthUser {
  if (data.user) return data.user;
  const id = data.userId ?? `user-${Date.now()}`;
  return {
    id,
    username: fallbackUsername,
    displayName: fallbackUsername,
    roles: [],
  };
}

function detectDeviceType(): string {
  // Browser only; mobile uses its own adapter.
  return 'WEB';
}

/* ── Active tenant ───────────────────────────────────────────────────── */

const TENANT_STORAGE_KEY = 'tradelogic.admin.tenant.v1';

function envTenantCode(): string {
  return (import.meta as { env?: { VITE_TENANT_CODE?: string } }).env?.VITE_TENANT_CODE?.trim() ?? '';
}

function readStoredTenantCode(): string {
  if (typeof window === 'undefined' || !window.localStorage) return '';
  try {
    return window.localStorage.getItem(TENANT_STORAGE_KEY)?.trim() ?? '';
  } catch {
    return '';
  }
}

let activeTenantCode: string = readStoredTenantCode();

/**
 * The tenant chosen on the sign-in screen. It only selects WHICH tenant to
 * authenticate against — it grants nothing on its own, and after sign-in the
 * JWT's tenant claim is authoritative. Persisted (not a secret) because
 * refresh/logout run long after the form is gone, including after a reload.
 */
export function setTenantCode(code: string): void {
  activeTenantCode = code.trim();
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    if (activeTenantCode) window.localStorage.setItem(TENANT_STORAGE_KEY, activeTenantCode);
    else window.localStorage.removeItem(TENANT_STORAGE_KEY);
  } catch { /* ignore quota / disabled storage */ }
}

/** Falls back to the build-time value so a single-tenant dev build still works. */
export function getTenantCode(): string {
  return activeTenantCode || envTenantCode();
}

function tenantHeaders(): Record<string, string> {
  const tenantCode = getTenantCode();
  return tenantCode ? { 'X-Tenant-Code': tenantCode } : {};
}

/* ── Public adapter functions ────────────────────────────────────────── */

export async function login(username: string, password: string, tenantCode?: string): Promise<NormalizedLogin> {
  // Set before the request so every later call — refresh, logout, verify-mfa —
  // uses the tenant the user actually chose rather than the build-time default.
  if (tenantCode !== undefined) setTenantCode(tenantCode);
  // Body matches LoginPasswordRequestDto exactly. Backend uses
  // StrictValidationPipe (whitelist=true, forbidNonWhitelisted=true) so
  // any extra field (e.g. legacy `username`) would 400. MSW handlers
  // accept `identifier` (see tests/msw/handlers.ts) so both work with
  // the single canonical field.
  const res = await apiRequest<LoginWire>('/v1/auth/login', {
    method: 'POST',
    headers: tenantHeaders(),
    body: {
      identifier: username,
      password,
      deviceType: detectDeviceType(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'server',
    },
  });

  // Backend MFA path
  if (res.mfa_required === true) {
    return {
      requiresMfa: true,
      mfaChallengeId: res.mfa_token ?? null,
      mfaMethod: res.mfa_method ?? null,
      token: null,
      refreshToken: null,
      user: null,
    };
  }
  // Legacy MSW MFA path
  if (res.requiresMfa === true) {
    return {
      requiresMfa: true,
      mfaChallengeId: res.mfaChallengeId ?? null,
      mfaMethod: null,
      token: null,
      refreshToken: null,
      user: null,
    };
  }

  // Backend success path
  if (res.data?.accessToken) {
    return {
      requiresMfa: false,
      mfaChallengeId: null,
      mfaMethod: null,
      token: res.data.accessToken,
      refreshToken: res.data.refreshToken ?? null,
      user: userFromTokenData(res.data, username),
    };
  }

  // Legacy MSW success path
  return {
    requiresMfa: false,
    mfaChallengeId: null,
    mfaMethod: null,
    token: res.token ?? null,
    refreshToken: null,
    user: res.user ?? null,
  };
}

export async function verifyMfa(
  mfaToken: string,
  code: string,
  method: string | null,
): Promise<NormalizedVerify> {
  // Body matches VerifyMfaDto exactly (forbidNonWhitelisted).
  const body = {
    mfaToken,
    mfaCode: code,
    mfaMethod: method ?? 'TOTP',
  };

  const res = await apiRequest<VerifyWire>('/v1/auth/verify-mfa', { method: 'POST', headers: tenantHeaders(), body });

  if (res.data?.accessToken) {
    return {
      token: res.data.accessToken,
      refreshToken: res.data.refreshToken ?? null,
      user: res.data.user ?? null,
    };
  }
  if (res.token && res.user) {
    return { token: res.token, refreshToken: null, user: res.user };
  }
  throw new ApiError(500, res, 'MFA verify: unexpected response shape');
}

/* ── Passwordless OTP login (§5 Phase 1: Mobile + OTP / Email + OTP) ──── */

export type OtpAuthMethod = 'MOBILE_OTP' | 'EMAIL_OTP';

export interface OtpRequestResult {
  readonly otpToken: string;
  readonly delivery: string | null;
  readonly expiresIn: number | null;
}

interface BackendOtpRequestResponse {
  readonly success?: boolean;
  readonly otp_token?: string;
  readonly delivery?: string;
  readonly expires_in?: number;
  // Legacy MSW shape.
  readonly otpToken?: string;
}

/**
 * Request a one-time passcode for a Mobile-OTP or Email-OTP login.
 * Body matches LoginOtpRequestDto exactly (identifier, authMethod,
 * deviceType) — StrictValidationPipe forbids non-whitelisted fields.
 * Returns the opaque `otpToken` that must be echoed back to verifyOtp().
 */
export async function requestOtp(
  identifier: string,
  authMethod: OtpAuthMethod,
  tenantCode?: string,
): Promise<OtpRequestResult> {
  if (tenantCode !== undefined) setTenantCode(tenantCode);
  const res = await apiRequest<BackendOtpRequestResponse>('/v1/auth/otp-request', {
    method: 'POST',
    headers: tenantHeaders(),
    body: {
      identifier,
      authMethod,
      deviceType: detectDeviceType(),
    },
  });
  const otpToken = res.otp_token ?? res.otpToken;
  if (!otpToken) throw new ApiError(500, res, 'OTP request: unexpected response shape');
  return {
    otpToken,
    delivery: res.delivery ?? null,
    expiresIn: res.expires_in ?? null,
  };
}

/**
 * Complete a passwordless OTP login. Body matches VerifyOtpDto exactly
 * (otpToken, otp). Normalises the backend `{ data }` and legacy `{ token,
 * user }` shapes down to NormalizedVerify.
 */
export async function verifyOtp(otpToken: string, otp: string): Promise<NormalizedVerify> {
  const res = await apiRequest<VerifyWire>('/v1/auth/verify-otp', {
    method: 'POST',
    headers: tenantHeaders(),
    body: { otpToken, otp },
  });
  if (res.data?.accessToken) {
    return {
      token: res.data.accessToken,
      refreshToken: res.data.refreshToken ?? null,
      user: res.data.user ?? null,
    };
  }
  if (res.token && res.user) {
    return { token: res.token, refreshToken: null, user: res.user };
  }
  throw new ApiError(500, res, 'OTP verify: unexpected response shape');
}

export type RefreshOutcome =
  | { readonly ok: true; readonly value: NormalizedVerify }
  | { readonly ok: false; readonly authFailed: boolean };

/**
 * Same call as {@link refresh}, but reports WHY it failed. `refresh()` collapses
 * every failure to null, so the caller cannot tell a rejected credential (the
 * session is genuinely over) from a network/5xx blip (it is not) and must
 * either sign the user out on transient errors or never sign them out at all.
 */
export async function refreshWithReason(): Promise<RefreshOutcome> {
  try {
    const res = await apiRequest<BackendVerifyResponse>('/v1/auth/refresh', {
      method: 'POST',
      headers: tenantHeaders(),
      body: {},
    });
    if (res.data?.accessToken) {
      return {
        ok: true,
        value: {
          token: res.data.accessToken,
          refreshToken: null, // never stored client-side anymore
          // Never synthesise an identity here: doing so replaced the signed-in user
          // with a placeholder named "user". Null lets the caller keep what it has.
          user: res.data.user ?? null,
        },
      };
    }
    return { ok: false, authFailed: false };
  } catch (e) {
    const status = e instanceof ApiError ? e.status : 0;
    return { ok: false, authFailed: status === 401 || status === 403 };
  }
}

export async function refresh(): Promise<NormalizedVerify | null> {
  // SEC-001: refresh token rides in the httpOnly `refresh_token` cookie
  // (set by the backend on /v1/auth/login|verify-mfa|verify-otp). The
  // body is intentionally empty — the browser attaches the cookie when
  // api/client.ts uses credentials:'include'. MSW handlers ignore the
  // body in tests.
  const out = await refreshWithReason();
  return out.ok ? out.value : null;
}

export async function forgot(username: string): Promise<{ resetTokenIssued: true }> {
  // The tenant travels in the same header sign-in uses.
  const body = { identifier: username };
  await apiRequest<unknown>('/v1/auth/forgot-password', { method: 'POST', headers: tenantHeaders(), body });
  return { resetTokenIssued: true } as const;
}

export async function changeExpiredPassword(changeToken: string, newPassword: string): Promise<{ changed: true }> {
  // The tenant travels in the same header every other auth call uses; the
  // backend resolves it with requireTenantId, so the grant can only be spent
  // against the tenant that issued it.
  await apiRequest<unknown>('/v1/auth/change-password', {
    method: 'POST',
    headers: tenantHeaders(),
    body: { changeToken, newPassword },
  });
  return { changed: true } as const;
}

export async function logout(): Promise<void> {
  // Best-effort: invalidate the server-side session. Failure is non-fatal
  // because the UI clears local state regardless.
  try {
    await apiRequest<unknown>('/v1/auth/logout', { method: 'POST', headers: tenantHeaders() });
  } catch {
    /* ignore */
  }
}
